import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import type { Errors } from '@inertiajs/core'
import { ImagePlus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TextInput } from './CreatorFields'
import type { Photo } from '@/pages/explore/wizard/types'

const MAX_PHOTOS = 10
const MAX_PHOTO_MB = 10
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const MAX_VIDEOS = 3

/**
 * A mídia do NEGÓCIO: a capa, a galeria e até 3 vídeos do YouTube.
 *
 * Vive em components/ e não dentro do wizard porque as duas telas do criador
 * usam a mesma coisa — o passo 5 de /nova-pagina e a aba "Fotos e vídeos" da
 * edição. Com duas cópias, o dia em que o limite mudar só uma das telas muda.
 *
 * As fotos não se confundem com as do catálogo, que mostram um produto: estas
 * mostram o lugar, a turma, a onda. E a CAPA é separada da galeria porque é ela
 * que vai no card do diretório, num recorte 16:9 — capa implícita ("a primeira
 * foto") é capa recortada errada.
 *
 * ATENÇÃO AO QUE SALVA QUANDO. Foto sobe na hora em que é escolhida (tem
 * endpoint próprio, porque é arquivo); link de vídeo é campo de formulário e só
 * persiste no submit de quem chama. Os dois comportamentos convivem na mesma
 * tela, e é por isso que a copy diz isso em voz alta: sem esse aviso, a pessoa
 * cola três links, sai da tela e perde os três.
 */
export default function FotosEVideos({
  pageId,
  cover,
  photos,
  videos,
  onVideosChange,
  videosError,
  intro,
  /**
   * De onde saem as rotas de anexo. Hoje é sempre /nova-pagina — os endpoints
   * aceitam qualquer página do criador, não só rascunho —, mas o prop existe
   * para o componente não fingir que só há um contexto.
   */
  basePath = '/nova-pagina',
}: {
  pageId: number
  cover: Photo | null
  photos: Photo[]
  videos: string[]
  onVideosChange: (next: string[]) => void
  videosError?: string | string[]
  intro?: string
  basePath?: string
}) {
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState<'cover' | 'photos' | null>(null)
  const coverRef = useRef<HTMLInputElement>(null)
  const galleryRef = useRef<HTMLInputElement>(null)

  const restantes = MAX_PHOTOS - photos.length
  const erroDeVideo = Array.isArray(videosError) ? videosError[0] : videosError

  // Mesma checagem do servidor, só que antes de gastar o upload: o arquivo
  // grande demais só seria recusado depois de subir inteiro.
  const invalida = (arquivos: File[]) => {
    const ruim = arquivos.find(
      (f) =>
        !PHOTO_TYPES.includes(f.type) || f.size > MAX_PHOTO_MB * 1024 * 1024,
    )
    if (!ruim) return null

    return !PHOTO_TYPES.includes(ruim.type)
      ? `${ruim.name}: envie JPEG, PNG, WebP ou GIF.`
      : `${ruim.name}: imagem muito grande (máximo ${MAX_PHOTO_MB}MB).`
  }

  const primeiroErro = (errors: Errors, fallback: string) => {
    const primeiro = Object.values(errors)[0]
    return (Array.isArray(primeiro) ? primeiro[0] : primeiro) ?? fallback
  }

  const enviar = (
    payload: { cover: File } | { photos: File[] },
    campo: 'cover' | 'photos',
    input: HTMLInputElement | null,
  ) => {
    setEnviando(campo)
    router.post(`${basePath}/${pageId}/fotos`, payload, {
      // Tem arquivo junto: sem isto o Inertia manda JSON e as fotos ficam pelo
      // caminho.
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => setErro(null),
      onError: (errors: Errors) =>
        setErro(primeiroErro(errors, 'Não foi possível enviar a foto.')),
      onFinish: () => {
        setEnviando(null)
        // Sem isto, escolher o MESMO arquivo de novo (depois de um erro) não
        // dispara `change` e a tela fica muda.
        if (input) input.value = ''
      },
    })
  }

  const escolherCapa = (lista: FileList | null) => {
    const arquivo = lista?.[0]
    if (!arquivo) return

    const problema = invalida([arquivo])
    if (problema) {
      setErro(problema)
      return
    }
    setErro(null)
    enviar({ cover: arquivo }, 'cover', coverRef.current)
  }

  const escolherFotos = (lista: FileList | null) => {
    if (!lista) return
    const escolhidas = Array.from(lista).slice(0, restantes)
    if (escolhidas.length === 0) return

    const problema = invalida(escolhidas)
    if (problema) {
      setErro(problema)
      return
    }
    setErro(null)
    enviar({ photos: escolhidas }, 'photos', galleryRef.current)
  }

  const remover = (url: string, aviso: string) => {
    router.delete(url, {
      preserveScroll: true,
      onError: (errors: Errors) => setErro(primeiroErro(errors, aviso)),
    })
  }

  return (
    <div className="space-y-6">
      {intro && <p className="max-w-2xl text-sm text-gray-600">{intro}</p>}

      <div>
        <p className="text-sm font-medium text-gray-700">Capa</p>
        <p className="mt-0.5 text-xs text-gray-500">
          A foto que representa o negócio. É ela que aparece no seu card.
        </p>

        <div className="mt-2 max-w-md">
          {cover ? (
            <span className="relative block">
              <img
                src={cover.url}
                alt="Capa da página"
                className="aspect-video w-full rounded-xl object-cover"
              />
              <button
                type="button"
                onClick={() =>
                  remover(
                    `${basePath}/${pageId}/capa`,
                    'Não foi possível remover a capa.',
                  )
                }
                aria-label="Remover capa"
                className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-1 text-gray-400 shadow ring-1 ring-gray-200 transition-colors hover:text-red-600"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => coverRef.current?.click()}
              disabled={enviando === 'cover'}
              className={cn(
                'flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 text-gray-400 transition-colors',
                'hover:border-primary hover:text-primary disabled:opacity-60',
              )}
            >
              <ImagePlus className="size-6" aria-hidden="true" />
              <span className="text-sm font-medium">
                {enviando === 'cover' ? 'Enviando…' : 'Escolher a capa'}
              </span>
            </button>
          )}
          <input
            ref={coverRef}
            type="file"
            accept={PHOTO_TYPES.join(',')}
            onChange={(e) => escolherCapa(e.target.files)}
            className="hidden"
          />
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700">
          Galeria (opcional) · {photos.length}/{MAX_PHOTOS}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          O lugar, a estrutura, o dia a dia — o que a pessoa quer ver antes de
          chamar você.
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {photos.map((foto) => (
            <span key={foto.id} className="relative block">
              <img
                src={foto.url}
                alt=""
                className="size-20 rounded-lg object-cover"
              />
              <button
                type="button"
                onClick={() =>
                  remover(
                    `${basePath}/${pageId}/fotos/${foto.id}`,
                    'Não foi possível remover a foto.',
                  )
                }
                aria-label="Remover foto da galeria"
                className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-gray-400 shadow ring-1 ring-gray-200 transition-colors hover:text-red-600"
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}

          {restantes > 0 && (
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              disabled={enviando === 'photos'}
              className="flex size-20 items-center justify-center rounded-lg border border-dashed border-gray-300 text-gray-400 transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
              aria-label="Adicionar fotos"
            >
              <ImagePlus className="size-5" aria-hidden="true" />
            </button>
          )}
          <input
            ref={galleryRef}
            type="file"
            accept={PHOTO_TYPES.join(',')}
            multiple
            onChange={(e) => escolherFotos(e.target.files)}
            className="hidden"
          />
        </div>

        {enviando === 'photos' && (
          <p className="mt-2 text-xs text-gray-500">Enviando…</p>
        )}
      </div>

      {erro && <p className="text-xs text-red-600">{erro}</p>}

      <div>
        <p className="text-sm font-medium text-gray-700">
          Vídeos do YouTube (opcional)
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          Até {MAX_VIDEOS} links. O <strong>primeiro</strong> aparece em
          destaque na sua página, junto com as fotos.
        </p>

        {/* O erro vem do servidor e diz QUAL link não serve. Sem renderizar
            aqui, o link errado volta em silêncio e a pessoa acha que salvou. */}
        {erroDeVideo && (
          <p className="mt-2 text-xs text-red-600">{erroDeVideo}</p>
        )}

        <div className="mt-2 space-y-2">
          {Array.from({ length: MAX_VIDEOS }, (_, index) => (
            <TextInput
              // Slots fixos por posição — a ordem É a identidade, porque é o
              // primeiro que vai para o destaque.
              key={index}
              value={videos[index] ?? ''}
              onChange={(v) => {
                const next = Array.from(
                  { length: MAX_VIDEOS },
                  (_, i) => videos[i] ?? '',
                )
                next[index] = v
                onVideosChange(next)
              }}
              // Na edição estes campos vivem dentro de um <form>, e Enter
              // dispararia o submit implícito no meio da digitação.
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault()
              }}
              placeholder={
                index === 0
                  ? 'Cole o link do vídeo em destaque'
                  : 'Cole o link de outro vídeo'
              }
            />
          ))}
        </div>

        <p className="mt-2 text-xs text-gray-500">
          As fotos salvam sozinhas assim que você escolhe. Os links de vídeo
          entram quando você salvar.
        </p>
      </div>
    </div>
  )
}
